// @refresh reset
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { FullScreenTakeover } from '../components/FullScreenTakeover';
import { DataExplorer } from '../components/DrilldownChart';
import type { Analysis } from '../services/analysis.service';

interface DataExplorerContextType {
  openDataExplorer: (params: {
    analysis: Analysis;
    originalAnalysisId: string;
    dateRange?: string;
    customDateStart?: string;
    customDateEnd?: string;
    onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
    onClose?: () => void;
  }) => void;
  closeDataExplorer: () => void;
}

const DataExplorerContext = createContext<DataExplorerContextType | undefined>(undefined);

interface DataExplorerProviderProps {
  children: ReactNode;
}

export function DataExplorerProvider({ children }: DataExplorerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [explorerParams, setExplorerParams] = useState<{
    analysis: Analysis;
    originalAnalysisId: string;
    dateRange?: string;
    customDateStart?: string;
    customDateEnd?: string;
    onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
    onClose?: () => void;
  } | null>(null);

  const openDataExplorer = useCallback((params: {
    analysis: Analysis;
    originalAnalysisId: string;
    dateRange?: string;
    customDateStart?: string;
    customDateEnd?: string;
    onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
    onClose?: () => void;
  }) => {
    setExplorerParams(params);
    setIsOpen(true);
  }, []);

  const closeDataExplorer = useCallback(() => {
    setIsOpen(false);
    // Call onClose callback if provided
    if (explorerParams?.onClose) {
      explorerParams.onClose();
    }
    // Clear params after a short delay to allow for smooth closing animation
    setTimeout(() => {
      setExplorerParams(null);
    }, 300);
  }, [explorerParams]);

  const handleAnalysisChange = useCallback((updatedAnalysis: Analysis) => {
    if (explorerParams) {
      setExplorerParams({
        ...explorerParams,
        analysis: updatedAnalysis,
      });
    }
  }, [explorerParams]);

  // Compute the title based on whether we're exploring an existing chart
  const getTitle = () => {
    if (!explorerParams) return 'Data Explorer';
    const isExistingChart = !!explorerParams.originalAnalysisId;
    const chartName = explorerParams.analysis?.name;
    if (isExistingChart && chartName) {
      return `Exploring: ${chartName}`;
    }
    return 'Data Explorer';
  };

  return (
    <DataExplorerContext.Provider value={{ openDataExplorer, closeDataExplorer }}>
      {children}
      {explorerParams && (
        <FullScreenTakeover
          show={isOpen}
          onHide={closeDataExplorer}
          noCloseButton={true}
          title={getTitle()}
        >
          <DataExplorer
            analysis={explorerParams.analysis}
            originalAnalysisId={explorerParams.originalAnalysisId}
            dateRange={explorerParams.dateRange}
            customDateStart={explorerParams.customDateStart}
            customDateEnd={explorerParams.customDateEnd}
            onDateRangeChange={explorerParams.onDateRangeChange}
            onAnalysisChange={handleAnalysisChange}
            onClose={closeDataExplorer}
          />
        </FullScreenTakeover>
      )}
    </DataExplorerContext.Provider>
  );
}

// Access Vite env vars in a way that TypeScript accepts
// Vite replaces import.meta.env at build time, so we access it with a type assertion
const getViteEnv = (): { DEV?: boolean; MODE?: string } => {
  // @ts-ignore - Vite provides import.meta.env at runtime, TypeScript doesn't recognize it with bundler module resolution
  return import.meta.env || {};
};

export function useDataExplorer() {
  const context = useContext(DataExplorerContext);
  const [retryCount, setRetryCount] = useState(0);
  
  // During hot reload, the context might be temporarily unavailable
  // Force a re-render when it becomes available again
  useEffect(() => {
    if (context === undefined) {
      // Context is undefined, schedule a retry
      const timeoutId = setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 100); // Check every 100ms
      
      return () => clearTimeout(timeoutId);
    }
  }, [context, retryCount]);
  
  if (context === undefined) {
    // Check if we're in development mode using Vite's env
    const env = getViteEnv();
    const isDev = env.DEV === true || env.MODE === 'development';
    
    if (isDev) {
      // Return stub functions that won't break the app
      // The useEffect above will trigger a re-render when context becomes available
      return {
        openDataExplorer: () => {
          console.warn('DataExplorerProvider is not available. openDataExplorer called but ignored.');
        },
        closeDataExplorer: () => {},
      };
    }
    throw new Error('useDataExplorer must be used within a DataExplorerProvider');
  }
  return context;
}

// Safe version that returns no-op functions when provider is not available
export function useDataExplorerSafe() {
  const context = useContext(DataExplorerContext);
  if (context === undefined) {
    // Return no-op functions when provider is not available
    return {
      openDataExplorer: () => {
        console.warn('DataExplorerProvider is not available. openDataExplorer called but ignored.');
      },
      closeDataExplorer: () => {},
    };
  }
  return context;
}

